import { ChangeEvent, FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  ModalProps,
  Stack,
  TextField,
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';

import { PaymentMethod } from '../../../../../enums';
import { DepositFiatWalletDTO, WithdrawFiatWalletDTO } from '../../../../../api';

export type WalletAction = 'deposit' | 'withdraw';
export type ActionFields = DepositFiatWalletDTO['body'] | WithdrawFiatWalletDTO['body'];

export type ActionModalProps = {
  open: boolean;
  onClose: () => void;
  walletAction: WalletAction | '';
  onSuccess: (values: ActionFields | null, resetFields: () => void) => void;
  isLoading: boolean;
};

export const ActionModal: FC<ActionModalProps> = (props) => {
  const { open, onClose, walletAction, onSuccess, isLoading } = props;
  const { t } = useTranslation();

  // fields logic
  const [fields, setFields] = useState<ActionFields | null>(null);
  const handleFields = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const name = e.target.name as keyof DepositFiatWalletDTO['body'];
    const value = e.target.value;

    // @ts-ignore
    setFields((prevState) => {
      if (!prevState) {
        return {
          [name]: value,
        };
      }

      return {
        ...prevState,
        [name]: value,
      };
    });
  };
  const resetFields = () => {
    setFields(null);
  };

  // validation for required fields
  const requiredFields = [
    'amount',
    'payment_method',
    'ext_transaction_id',
    'password',
  ] as (keyof DepositFiatWalletDTO['body'])[];
  const checkSubmitDisabling = () => {
    if (!fields) return true;

    for (const field of requiredFields) {
      if (fields[field] === undefined || fields[field] === '') {
        return true;
      }
    }
    return false;
  };

  // close handlers
  const closeHandlerWithPrevent: ModalProps['onClose'] = (_, reason) => {
    if (reason === 'backdropClick') return;
    setFields(null);
    onClose();
  };
  const closeHandler = () => {
    setFields(null);
    onClose();
  };

  // elements
  const paymentMethodOptions = (
    <MenuItem value={PaymentMethod.BANK}>
      {t(`common.payment-method.${PaymentMethod.BANK}`)}
    </MenuItem>
  );

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => closeHandlerWithPrevent(_, reason)}
      PaperProps={{ sx: { minWidth: '380px' } }}
    >
      <DialogTitle>
        {walletAction ? t(`profile-wallets.actions.${walletAction}.modal.title`) : '-'}
      </DialogTitle>
      <IconButton
        aria-label="close"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: (theme) => theme.palette.grey[500],
        }}
        onClick={() => closeHandler()}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent>
        <Stack direction="column" spacing={2}>
          <TextField
            name="amount"
            value={fields?.amount ?? ''}
            onChange={(e) => handleFields(e)}
            label={
              walletAction
                ? t(`profile-wallets.actions.${walletAction}.modal.fields.amount`)
                : undefined
            }
            fullWidth
          />
          <TextField
            select
            name="payment_method"
            value={fields?.payment_method ?? ''}
            onChange={(e) => handleFields(e)}
            label={
              walletAction
                ? t(`profile-wallets.actions.${walletAction}.modal.fields.payment-method`)
                : undefined
            }
            fullWidth
          >
            {paymentMethodOptions}
          </TextField>
          <TextField
            name="ext_transaction_id"
            value={fields?.ext_transaction_id ?? ''}
            onChange={(e) => handleFields(e)}
            label={
              walletAction
                ? t(`profile-wallets.actions.${walletAction}.modal.fields.ext-transaction-id`)
                : undefined
            }
            fullWidth
          />
          {walletAction === 'deposit' && (
            <TextField
              name="password"
              value={fields?.password ?? ''}
              onChange={(e) => handleFields(e)}
              label={
                walletAction
                  ? t(`profile-wallets.actions.${walletAction}.modal.fields.password`)
                  : undefined
              }
              fullWidth
            />
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        {walletAction ? (
          <>
            <Button
              variant="contained"
              onClick={() => onSuccess(fields, resetFields)}
              disabled={isLoading || checkSubmitDisabling()}
            >
              {t(`profile-wallets.actions.${walletAction}.modal.btn.${walletAction}`)}
            </Button>
            <Button variant="outlined" onClick={() => closeHandler()} disabled={isLoading}>
              {t(`profile-wallets.actions.${walletAction}.modal.btn.cancel`)}
            </Button>{' '}
          </>
        ) : (
          <div />
        )}
      </DialogActions>
    </Dialog>
  );
};
